import { useCallback, useRef } from 'react';

const FRAME_DURATION = 1 / 30;
const HOLD_INITIAL_DELAY = 400;
const HOLD_INITIAL_INTERVAL = 100;
const HOLD_FAST_INTERVAL = 50;
const HOLD_ACCELERATION_TIME = 1000;

const usePlaybackActions = (containerRef) => {
    const holdIntervalRef = useRef(null);
    const holdStartTimeRef = useRef(null);
    const holdTimeoutRef = useRef(null);

    const getVideo = useCallback(() => {
        return containerRef?.current?.querySelector('video') || null;
    }, [containerRef]);

    const handlePlayPause = useCallback(() => {
        const video = getVideo();
        if (video) {
            if (video.paused) {
                video.play();
            } else {
                video.pause();
            }
        }
    }, [getVideo]);

    const handleSkipBack30 = useCallback(() => {
        const video = getVideo();
        if (video) {
            video.currentTime = Math.max(0, video.currentTime - 30);
        }
    }, [getVideo]);

    const handleSkipForward30 = useCallback(() => {
        const video = getVideo();
        if (video) {
            video.currentTime = Math.min(video.duration || 0, video.currentTime + 30);
        }
    }, [getVideo]);

    const handleSkipBack5 = useCallback(() => {
        const video = getVideo();
        if (video) {
            video.currentTime = Math.max(0, video.currentTime - 5);
        }
    }, [getVideo]);

    const handleSkipForward5 = useCallback(() => {
        const video = getVideo();
        if (video) {
            video.currentTime = Math.min(video.duration || 0, video.currentTime + 5);
        }
    }, [getVideo]);

    const stepFrame = useCallback(
        (direction) => {
            const video = getVideo();
            if (!video) return;

            if (!video.paused) {
                video.pause();
            }

            const frameStep = direction === 'prev' ? -FRAME_DURATION : FRAME_DURATION;
            const newTime = video.currentTime + frameStep;

            if (direction === 'prev') {
                video.currentTime = Math.max(0, newTime);
            } else {
                video.currentTime = Math.min(video.duration || 0, newTime);
            }
        },
        [getVideo]
    );

    const clearHoldTimers = useCallback(() => {
        if (holdTimeoutRef.current) {
            clearTimeout(holdTimeoutRef.current);
            holdTimeoutRef.current = null;
        }
        if (holdIntervalRef.current) {
            clearInterval(holdIntervalRef.current);
            holdIntervalRef.current = null;
        }
        holdStartTimeRef.current = null;
    }, []);

    const startHoldFrame = useCallback(
        (direction) => {
            stepFrame(direction);

            holdStartTimeRef.current = Date.now();

            holdTimeoutRef.current = setTimeout(() => {
                const runContinuousStep = () => {
                    const elapsed = Date.now() - holdStartTimeRef.current;
                    const interval = elapsed > HOLD_ACCELERATION_TIME ? HOLD_FAST_INTERVAL : HOLD_INITIAL_INTERVAL;

                    stepFrame(direction);

                    holdIntervalRef.current = setTimeout(runContinuousStep, interval);
                };

                runContinuousStep();
            }, HOLD_INITIAL_DELAY);
        },
        [stepFrame]
    );

    const stopHoldFrame = useCallback(() => {
        clearHoldTimers();
    }, [clearHoldTimers]);

    const handlePrevFrameMouseDown = useCallback(() => {
        startHoldFrame('prev');
    }, [startHoldFrame]);

    const handleNextFrameMouseDown = useCallback(() => {
        startHoldFrame('next');
    }, [startHoldFrame]);

    const handleTakeSnapshot = useCallback(() => {
        const video = getVideo();

        try {
            const canvas = document.createElement('canvas');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            const base64 = canvas.toDataURL('image/jpeg', 1);
            return base64;
        } catch (error) {
            console.error('[Playback Snapshot] Failed to take snapshot:', error);
            return null;
        }
    }, [getVideo]);

    return {
        handlePlayPause,
        handleSkipBack30,
        handleSkipForward30,
        handleSkipBack5,
        handleSkipForward5,
        handlePrevFrameMouseDown,
        handleNextFrameMouseDown,
        stopHoldFrame,
        handleTakeSnapshot,
    };
};

export default usePlaybackActions;
